import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Textarea,
  VStack,
  Text,
  Input,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import { ParsedChartData } from '../../../models/types';
import { extractCompleteBirthData } from '../../../utils/datetime/dateTimeParser';
import { jatakaApiService } from '../../../services/JatakaApiService';
import {
  birthDataToJatakaRequest,
  jatakaResponseToChartData,
  BirthData as ApiBirthData
} from '../../../utils/apiDataTransformers';
import { getStorageService } from '../../../services/StorageService';
import ImportErrorDisplay from './ImportErrorDisplay';

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (data: ParsedChartData, savedChartId?: string) => void;
}

/**
 * Modal component for importing astrological data from clipboard
 */
const ImportModal: React.FC<ImportModalProps> = ({ isOpen, onClose, onImport }) => {
  const [importData, setImportData] = useState('');
  const [chartName, setChartName] = useState('Imported Chart');
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const placeholderText = `Paste your Natal Chart data here. Expected format:

Natal Chart

Date:          July 6, 2025
Time:          23:33:01
Time Zone:     3:00:00 (East of GMT)
Place:         34 E 33' 00", 48 N 35' 42"
               kam, Ukraine
Altitude:      0.00 meters

Lunar Yr-Mo:   Viswa-vasu - Ashadha
...

The system will extract date, time, coordinates and timezone, then calculate planetary positions via API.`;

  // Auto-populate chart name from first line of import data
  useEffect(() => {
    if (importData.trim()) {
      const lines = importData.trim().split('\n');
      const firstLine = lines[0]?.trim();
      
      if (firstLine && !firstLine.toLowerCase().includes('natal chart')) {
        // Extract filename from path (remove directory path)
        let cleanName = firstLine;
        
        // Remove file paths (Windows and Unix style)
        if (cleanName.includes('\\') || cleanName.includes('/')) {
          const pathParts = cleanName.split(/[\\\/]/);
          cleanName = pathParts[pathParts.length - 1]; // Get last part (filename)
        }
        
        // Remove file extensions
        cleanName = cleanName.replace(/\.(txt|dat|jhr|jhd|jh|asc)$/i, '');
        
        // Use cleaned filename as chart name
        if (cleanName) {
          setChartName(cleanName);
        }
      } else if (lines.length > 1) {
        // If first line is "Natal Chart", try to use location or other info
        const locationLine = lines.find(line => 
          line.toLowerCase().includes('place:') || 
          (line.includes(',') && !line.includes(':')));
        if (locationLine) {
          const cleanName = locationLine.replace(/^Place:\s*/i, '').trim();
          if (cleanName) {
            setChartName(`Chart - ${cleanName}`);
          }
        }
      }
    }
  }, [importData]);

  const handleImport = async () => {
    if (!importData.trim()) {
      setError('Please paste some data to import');
      return;
    }

    setIsLoading(true);
    setError(null);
    setWarnings([]);

    try {
      // Extract birth data from Natal Chart format
      const extractionResult = extractCompleteBirthData(importData);
      
      if (!extractionResult.success || !extractionResult.data) {
        setError(extractionResult.error || 'Failed to extract birth data from import');
        return;
      }
      
      const { chartDateTime, latitude, longitude } = extractionResult.data;
      
      // Convert to API format and calculate chart via Jataka API
      const birthData: ApiBirthData = {
        chartName: chartName.trim() || 'Imported Chart',
        locationName: chartDateTime.location || '',
        date: chartDateTime.date.split('-').reverse().join('-'), // Convert YYYY-MM-DD to DD-MM-YYYY
        time: chartDateTime.time,
        latitude,
        longitude,
        timezoneMode: 'manual' as const,
        timezoneOffset: chartDateTime.timezone,
        selectedLocation: chartDateTime.location || ''
      };
      
      // Call Jataka API to get planetary data
      const apiRequest = birthDataToJatakaRequest(birthData);
      const apiResult = await jatakaApiService.calculateJataka(apiRequest);
      
      if (!apiResult.success || !apiResult.data) {
        const errorMessage = apiResult.error?.message || 'Failed to calculate chart via API';
        setError(`API calculation failed: ${errorMessage}`);
        return;
      }
      
      // Convert API response to chart data
      const chartData = jatakaResponseToChartData(apiResult.data, birthData);
      
      // Immediately save and import the chart
      let savedChartId: string | undefined;
      
      // Save to storage if available
      const storageService = getStorageService();
      if (storageService.isInitialized()) {
        const saveResult = await storageService.saveChart(chartName.trim(), chartData);
        if (saveResult.success && saveResult.data) {
          savedChartId = saveResult.data;
        }
      }

      // Call the import function immediately
      await onImport(chartData, savedChartId);
      
      // Reset form and close modal on success
      handleCancel();
      
      
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('Invalid coordinate format')) {
          setError('Invalid coordinate format detected. Please ensure data is copied correctly from Natal Chart.');
        } else if (err.message.includes('Could not extract')) {
          setError('Could not extract required information. Please check the data format.');
        } else {
          setError(`Import error: ${err.message}`);
        }
      } else {
        setError('An unexpected error occurred during import. Please check your data format.');
      }
    } finally {
      setIsLoading(false);
    }
  };


  const handleCancel = () => {
    setImportData('');
    setChartName('Imported Chart');
    setError(null);
    setWarnings([]);
    onClose();
  };

  const storageService = getStorageService();
  const storageAvailable = storageService.isInitialized();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Import Chart Data</ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="gray.600">
              Paste your Natal Chart data below. The system will extract date, time, coordinates 
              and timezone, then calculate planetary positions using Swiss Ephemeris via API.
            </Text>
            
            <ImportErrorDisplay 
              error={error}
              warnings={warnings}
              showDetails={true}
            />
            
            <FormControl>
              <FormLabel fontSize="sm">Chart Name</FormLabel>
              <Input
                value={chartName}
                onChange={(e) => setChartName(e.target.value)}
                placeholder="Enter name for this chart"
                size="sm"
              />
            </FormControl>
            
            <Textarea
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              placeholder={placeholderText}
              size="sm"
              minHeight="300px"
              fontFamily="var(--font-ui-mono)"
              fontSize="sm"
              resize="vertical"
            />
            
            {!storageAvailable && (
              <Alert status="warning" size="sm">
                <AlertIcon />
                <Text fontSize="xs">
                  Local storage unavailable. Chart will be imported but not saved.
                </Text>
              </Alert>
            )}
            
            <Text fontSize="xs" color="gray.500">
              The system will calculate all planetary positions and Lagna using precise Swiss Ephemeris data.
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button 
            variant="ghost" 
            mr={3} 
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          
          <Button 
            colorScheme="blue" 
            onClick={handleImport}
            isLoading={isLoading}
            loadingText="Importing..."
            disabled={!importData.trim() || !chartName.trim()}
          >
            Import Chart
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ImportModal;